import { CheckIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { formatWhatsappText } from 'src/functions/whatsappFormat.render';
import { useTranslation } from 'react-i18next';
import { MediaType, MessageState } from 'src/components/Chat/Chat.enum.ts';
import {
  META_ERROR_NOTICE_NAMESPACE,
  noticeStatusKey,
} from 'src/utils/metaErrorNotice.ts';
import { formatDate, formatTime } from '../../../../../../Chat.functions.ts';
import { useChatContext } from '../../../../../../Chat.hook.ts';
import { tAttachment, tChatEvent } from '../../../../../../Chat.types.ts';
import useMessageBubbleMenu from '../MessageBubbleMenu/MessageBubbleMenu.hook.tsx';
import AudioContent from '../MessageBubble/components/AudioContent/AudioContent.component.tsx';
import FileContent from '../MessageBubble/components/FileContent/FileContent.component.tsx';
import ImageContent from '../MessageBubble/components/ImageContent/ImageContent.component.tsx';
import LocationContent from '../MessageBubble/components/LocationContent/LocationContent.component.tsx';
import StickerContent from '../MessageBubble/components/StickerContent/StickerContent.component.tsx';
import VideoContent from '../MessageBubble/components/VideoContent/VideoContent.component.tsx';
import './TemplateBubble.styles.scss';
import { Button } from 'react-daisyui';
import TemplateCarouselPreview from 'src/pages/WhatsappApi/components/TemplateCarouselPreview/TemplateCarouselPreview.component.tsx';
import {
  buildCarouselComponents,
  resolveTemplateMetaErrorNotice,
} from './TemplateBubble.functions.ts';
import ScheduledLegend from '../ScheduledLegend/ScheduledLegend.component.tsx';
import CatalogMetaErrorNotice from '../MessageBubble/components/MetaErrorNotice/CatalogMetaErrorNotice.component.tsx';
import {
  findResolvingMessage,
  resolvedTimeFormat,
} from '../MessageBubble/MessageBubbleV2.functions.ts';

export type tTemplateBubbleProps = tChatEvent & {
  /** INT-1505: the scheduled card this template was sent from, if any. */
  scheduledOrigin?: tChatEvent;
};

function RenderFileType({ attachment }: { attachment: tAttachment }) {
  switch (attachment.mediaType) {
    case MediaType.IMAGE:
      return <ImageContent attachment={attachment} />;
    case MediaType.VIDEO:
      return <VideoContent attachment={attachment} />;
    case MediaType.AUDIO:
      return <AudioContent attachment={attachment} />;
    case MediaType.STICKER:
      return <StickerContent attachment={attachment} />;
    case MediaType.DOCUMENT:
      return <FileContent attachment={attachment} />;
    case MediaType.LOCATION:
      return (
        <LocationContent
          latitude={attachment.latitude ?? 0}
          longitude={attachment.longitude ?? 0}
        />
      );
    default:
      return <FileContent attachment={attachment} />;
  }
}

export default function TemplateBubbleComponent(props: tTemplateBubbleProps) {
  const { selectedChat, chatEvents } = useChatContext();
  const { t } = useTranslation(META_ERROR_NOTICE_NAMESPACE);

  // INT-1500: a template Meta refused or holds for review says so inside the
  // bubble, like a message (INT-1503), instead of a red icon with a hover
  // tooltip. Most refusals are on templates (131049, 130472, 131050, 132xxx),
  // and every template in the chat renders here, scheduled ones included.
  const metaErrorNotice = resolveTemplateMetaErrorNotice(
    props,
    selectedChat?.integration,
  );
  // INT-1504: a later message got through, so the notice has done its job. A
  // template in review did not fail, so nothing resolves it.
  const resolvingMessage =
    metaErrorNotice && metaErrorNotice.variant !== 'pending'
      ? findResolvingMessage(props, chatEvents ?? [])
      : undefined;
  const showMetaErrorNotice = Boolean(metaErrorNotice) && !resolvingMessage;

  const { showCheckStatus } = useMessageBubbleMenu();

  const attachment = props.content?.attachments?.[0];
  // INT-1302 — undefined for every non-carousel template, so the preview
  // renders nothing and every existing bubble stays byte-identical.
  const carouselComponents = buildCarouselComponents(props.content);

  return (
    <div
      className={classNames('TemplateBubble', {
        'TemplateBubble--with-notice': showMetaErrorNotice,
      })}
    >
      <div className="TemplateBubble__Header">
        <div className="TemplateBubble__Header__Container">
          <h1 className="TemplateBubble__Header__Container__Title">
            {props?.note?.title ?? 'Template enviado'}
          </h1>
        </div>
      </div>
      {attachment && <RenderFileType attachment={attachment} />}
      {/* INT-1500 U10: a template refused before it went out may carry no
          rendered text; no empty line for it. */}
      {props.content?.text ? (
        <p className="TemplateBubble__Text break-words whitespace-pre-wrap">
          {formatWhatsappText(props.content.text.replace(/\\n/g, '\n'))}
        </p>
      ) : null}
      {/* INT-1302 — the sender's own carousel, drawn with the same shared
          preview the chat's template picker already uses. `min-w-0` is
          load-bearing: the strip scrolls horizontally, and without it a flex
          child's min-content width can push this bubble past its
          max-w-[60%] w-fit box. */}
      {carouselComponents ? (
        <div className="min-w-0 w-full">
          <TemplateCarouselPreview components={carouselComponents} />
        </div>
      ) : null}
      <div className="flex  gap-2 w-full flex-wrap">
        {Array.isArray(props.content?.buttons) &&
          props.content.buttons.length > 0 &&
          props.content.buttons.map((button, index) => {
            return (
              <Button
                variant="outline"
                color="primary"
                type="button"
                key={button.text + index}
                className="w-full"
              >
                {button.text}
              </Button>
            );
          })}
      </div>
      {props.scheduledOrigin && (
        <ScheduledLegend
          followUp={props.scheduledOrigin}
          className="TemplateBubble__Legend"
        />
      )}
      <div className="TemplateBubble__Bottom">
        <span className="TemplateBubble__Bottom__Sender">
          {
            selectedChat?.lead?.users?.find(
              (user) => user.auth_id === props.createdBy,
            )?.name
          }
        </span>
        <span className="TemplateBubble__Bottom__Date">
          {formatDate(props?.createdAt ?? '')} |{' '}
          {formatTime(props?.createdAt ?? '')}
        </span>
        {/* INT-1500: "· No entregado", "· No enviado" (Leadsales refused
            it, U10) or "· En revisión" stands in for the status icon, as on
            a message (INT-1503, U5). */}
        {metaErrorNotice?.variant === 'pending' ? (
          <span className="TemplateBubble__Bottom__InReview">
            · {t('status.inReview')}
          </span>
        ) : metaErrorNotice ? (
          <span className="TemplateBubble__Bottom__NotDelivered">
            · {t(noticeStatusKey(metaErrorNotice))}
          </span>
        ) : (
          <span className="TemplateBubble__Bottom__Status">
            {showCheckStatus(
              props?.message?.state ?? MessageState.SEND_PENDING,
              props.message?.error ?? '',
            )}
          </span>
        )}
      </div>
      {metaErrorNotice && showMetaErrorNotice && (
        <CatalogMetaErrorNotice notice={metaErrorNotice} eventId={props.id} />
      )}
      {resolvingMessage && (
        <p className="TemplateBubble__Resolved" data-testid="meta-error-resolved">
          <CheckIcon className="TemplateBubble__Resolved__Icon" aria-hidden="true" />
          <span>
            {t('resolved', { time: resolvedTimeFormat(resolvingMessage.createdAt) })}
          </span>
        </p>
      )}
    </div>
  );
}
